import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import qrcode from '../../vendor/qrcode-generator/qrcode.mjs';
import { Delete, FaceID } from './icons';

// ---- Brand ----

export const BrandMark = ({ size = 32 }: { size?: number }) => (
  <img src="/icons/logo-mark.png" width={size} height={size} alt="" class="brand-mark" />
);

export const BrandWordmark = ({ width = 200 }: { width?: number }) => (
  <img src="/icons/logo-wordmark.png" width={width} height={Math.round((width * 85) / 835)} alt="COINWOW" class="brand-wordmark" />
);

/** Emblem on top, wordmark below, the wordmark's middle dot centred under the coin. */
export const BrandLockup = ({ markSize = 104 }: { markSize?: number }) => (
  <div class="lockup" style={{ gap: `${markSize * 0.42}px` }}>
    <BrandMark size={markSize} />
    <BrandWordmark width={markSize * 2.6} />
  </div>
);

// ---- Buttons ----

type BtnProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & { children: ComponentChildren };

export const PrimaryButton = ({ class: c, ...p }: BtnProps) => <button type="button" class={`btn btn-primary ${c ?? ''}`} {...p} />;
export const SecondaryButton = ({ class: c, ...p }: BtnProps) => <button type="button" class={`btn btn-secondary ${c ?? ''}`} {...p} />;
export const PlainButton = ({ class: c, ...p }: BtnProps) => <button type="button" class={`plain ${c ?? ''}`} {...p} />;

// ---- Layout ----

export const Card = ({ children, class: c }: { children: ComponentChildren; class?: string }) => (
  <div class={`card ${c ?? ''}`}>{children}</div>
);

export const SectionTitle = ({ children }: { children: ComponentChildren }) => <div class="section-title">{children}</div>;

/** Grouped list like an iOS Form section. */
export const Group = ({ title, footer, children }: { title?: string; footer?: ComponentChildren; children: ComponentChildren }) => (
  <section class="group">
    {title && <div class="group-title">{title}</div>}
    <div class="group-body">{children}</div>
    {footer && <div class="group-footer">{footer}</div>}
  </section>
);

export const Row = ({ icon, label, value, onClick, danger, chevron, children }: {
  icon?: ComponentChildren; label?: ComponentChildren; value?: ComponentChildren; onClick?: () => void;
  danger?: boolean; chevron?: boolean; children?: ComponentChildren;
}) => {
  const inner = (
    <>
      {icon && <span class="row-icon">{icon}</span>}
      {label !== undefined && <span class="row-label">{label}</span>}
      {children}
      {value !== undefined && <span class="row-value">{value}</span>}
      {chevron && <span class="row-chevron">›</span>}
    </>
  );
  return onClick
    ? <button type="button" class={`row row-button ${danger ? 'danger' : ''}`} onClick={onClick}>{inner}</button>
    : <div class={`row ${danger ? 'danger' : ''}`}>{inner}</div>;
};

export const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} class={`toggle ${checked ? 'on' : ''}`}
    onClick={() => onChange(!checked)}>
    <span class="knob" />
  </button>
);

/** Full-screen sheet with a header bar (like an iOS sheet with a NavigationStack). */
export const Sheet = ({ title, left, right, children, class: c }: {
  title?: ComponentChildren; left?: ComponentChildren; right?: ComponentChildren; children: ComponentChildren; class?: string;
}) => (
  <div class={`sheet ${c ?? ''}`} role="dialog" aria-modal="true">
    <header class="sheet-header">
      <div class="sheet-left">{left}</div>
      <div class="sheet-title">{title}</div>
      <div class="sheet-right">{right}</div>
    </header>
    <div class="sheet-body">{children}</div>
  </div>
);

export const Spinner = () => <span class="spinner" aria-label="…" />;

// ---- QR code ----

export function QRCodeView({ text, size = 220, label }: { text: string; size?: number; label?: string }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    let d = '';
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    return { n, d };
  }, [text]);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${cells.n} ${cells.n}`} shape-rendering="crispEdges" role="img" aria-label={label}>
      <path d={cells.d} fill="#000" />
    </svg>
  );
}

// ---- Passcode pad ----

export function PinPad({ title, subtitle, error, onComplete, faceID, onFaceID, busy, footer }: {
  title: string;
  subtitle?: string;
  error?: string | null;
  /** Called with the 6 digits; the pad clears afterwards. */
  onComplete: (pin: string) => void | Promise<void>;
  faceID?: boolean;
  onFaceID?: () => void;
  busy?: boolean;
  footer?: ComponentChildren;
}) {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (error) {
      setShake(true);
      const t = setTimeout(() => setShake(false), 450);
      return () => clearTimeout(t);
    }
  }, [error]);

  const press = async (d: string) => {
    if (busy) return;
    const next = (pin + d).slice(0, 6);
    setPin(next);
    if (next.length === 6) {
      await onComplete(next);
      setPin('');
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) void press(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div class="pinpad">
      <div class="pin-title">{title}</div>
      {subtitle && <div class="pin-subtitle">{subtitle}</div>}
      <div class={`pin-dots ${shake ? 'shake' : ''}`} aria-label={`${pin.length}/6`}>
        {[0, 1, 2, 3, 4, 5].map((i) => <span class={i < pin.length ? 'filled' : ''} />)}
      </div>
      <div class="pin-error" role="alert">{busy ? <Spinner /> : error ?? ''}</div>
      <div class="pin-keys">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button type="button" class="pin-key" onClick={() => press(d)}>{d}</button>
        ))}
        {faceID && onFaceID
          ? <button type="button" class="pin-key pin-aux" onClick={onFaceID} aria-label="Face ID"><FaceID size={30} /></button>
          : <span />}
        <button type="button" class="pin-key" onClick={() => press('0')}>0</button>
        <button type="button" class="pin-key pin-aux" onClick={() => setPin((p) => p.slice(0, -1))} aria-label="⌫"><Delete size={26} /></button>
      </div>
      {footer}
    </div>
  );
}

export function copyText(text: string): Promise<void> {
  return navigator.clipboard?.writeText(text) ?? Promise.reject(new Error('clipboard unavailable'));
}

export function haptic() {
  try {
    navigator.vibrate?.(15);
  } catch {}
}
